import BigNumber from 'bignumber.js';
import cn from 'classnames';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Checkbox, Input, Selector, Switch } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import {
  agencyFeeModel as agencyFeeModels,
  platformFeeModel as platformFeeModels
} from 'sonata-static-storage/lib/samples/pricingModels.samples';
import { PricingModelEnum } from 'sonata-static-storage/lib/schemas/campaign/bidModel.schema';
import { MediaTypeEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';
import { dCpmModel } from 'sonata-static-storage/lib/schemas/user/user.schema';

import { usePricingModel } from '../PricingModel.context';
import { PricingAdvanceSettingsStyled } from './PricingAdvanceSettings.styled';

import type { MarginProp, NumberProp, StringProp } from '../pricingModel.types';

type Props = {
  id: string;
};

const COMMON_INPUT_PROPS: Pick<
  React.ComponentProps<typeof Input>,
  'allowPercentageOverflow' | 'numberFormat' | 'numberOfDecimals'
> = {
  allowPercentageOverflow: true,
  numberFormat: 'percentage',
  numberOfDecimals: 2
};

function PricingAdvanceSettings({ id }: Props) {
  const { formatMessage } = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const { errors, mediaType, pricingModel, pricingModelConfig, setProp, user, userRoles } = usePricingModel();
  const { agencyFeeModel, dCPMModel, margin, marginCPM, markup, platformFee, platformFeeModel } = pricingModelConfig;

  const campaignMarginPermission = checkUserResourcePermissions('campaignMargin', user, userRoles);
  const campaignMarkupPermission = checkUserResourcePermissions('campaignMarkup', user, userRoles);
  const agencyFeeModelPermission = checkUserResourcePermissions('agencyFeeModel', user, userRoles);
  const platformFeePermission = checkUserResourcePermissions('platformFee', user, userRoles);
  const platformFeeModelPermission = checkUserResourcePermissions('platformFeeModel', user, userRoles);
  const marginCPMPermission = checkUserResourcePermissions('marginCPM', user, userRoles);
  const dCPMModelPermission = checkUserResourcePermissions('dCPMModel', user, userRoles);
  const advanceSettingsPermission =
    campaignMarginPermission ||
    campaignMarkupPermission ||
    agencyFeeModelPermission ||
    platformFeePermission ||
    marginCPMPermission ||
    dCPMModelPermission;

  const handleMarginChange = (prop: MarginProp, value: string, currentMediaType = mediaType) => {
    setProp(prop, {
      ...pricingModelConfig[prop],
      [currentMediaType]: new BigNumber(value).decimalPlaces(4).toNumber() || 0
    });
  };

  const handleModelChange = (prop: StringProp, value: { id: string }) => {
    setProp(prop, value.id);
  };

  const handleNumberChange = (prop: NumberProp, value: string) => {
    setProp(prop, new BigNumber(value).decimalPlaces(4).toNumber() || 0);
  };

  const agencyFeeModelOptions = agencyFeeModels.map(agencyModel => ({
    id: agencyModel.model,
    label: formatMessage({
      defaultMessage: agencyModel.model,
      id: `pricingModel.advanceSettings.agencyFeeModels.${agencyModel.model}`
    })
  }));

  const agencyFeeModelSelectedOptions = agencyFeeModelOptions.filter(opt => opt.id === agencyFeeModel);

  const platformFeeModelOptions = platformFeeModels.map(platformModel => ({
    id: platformModel.model,
    label: formatMessage({
      defaultMessage: platformModel.model,
      id: `pricingModel.advanceSettings.platformFeeModels.${platformModel.model}`
    })
  }));

  const platformFeeModelSelectedOptions = platformFeeModelOptions.filter(opt => opt.id === platformFeeModel);

  const dCPMOptions = Object.values(dCpmModel).map(dcpmModelKey => ({
    id: dcpmModelKey,
    label: formatMessage({
      defaultMessage: dcpmModelKey,
      id: `pricingModel.advanceSettings.DCPMModels.${dcpmModelKey}`
    })
  }));

  const dCPMSelectedOptions = dCPMOptions.filter(opt => opt.id === dCPMModel);

  if (!advanceSettingsPermission) {
    return null;
  }

  return (
    <PricingAdvanceSettingsStyled className="pricingAdvanceSettings" id={id}>
      <Checkbox
        active={isOpen}
        classes="pricingAdvanceSettings__titleToggle"
        colorSchema="secondary"
        id={`advance-settings-${id}`}
        labelText={formatMessage({
          defaultMessage: 'Pricing Advanced Settings',
          id: 'pricingModel.advanceSettings.title'
        })}
        onClickAction={(_, checked) => setIsOpen(checked)}
        type="switch"
      />
      <div
        className={cn('pricingAdvanceSettings__body', {
          'pricingAdvanceSettings__body--hide': !isOpen
        })}
      >
        {campaignMarginPermission && (
          <Switch>
            <Switch.Case condition={mediaType === MediaTypeEnum.hybrid}>
              <Input
                {...COMMON_INPUT_PROPS}
                description={errors?.margin?.openRtb?.message}
                id={`campaign-margin-open-${id}`}
                inputStatus={errors?.margin?.openRtb ? 'wrong' : 'default'}
                label={`${formatMessage({
                  defaultMessage: 'Campaign Margin',
                  id: 'pricingModel.advanceSettings.campaignMarginLabel'
                })}${
                  mediaType === MediaTypeEnum.hybrid
                    ? ` (${formatMessage({
                        defaultMessage: 'Open',
                        id: 'pricingModel.advanceSettings.openSuffixLabel'
                      })})`
                    : ''
                }`}
                name="campaign-margin-open"
                numberOfDecimals={2}
                onChange={value => handleMarginChange('margin', value as string, MediaTypeEnum.openRtb)}
                shape="squareUI"
                type="number"
                value={margin.openRtb}
              />
              <Input
                {...COMMON_INPUT_PROPS}
                description={errors?.margin?.pmp?.message}
                id={`campaign-margin-premium-${id}`}
                inputStatus={errors?.margin?.pmp ? 'wrong' : 'default'}
                label={`${formatMessage({
                  defaultMessage: 'Campaign Margin',
                  id: 'pricingModel.advanceSettings.campaignMarginLabel'
                })}${
                  mediaType === MediaTypeEnum.hybrid
                    ? ` (${formatMessage({
                        defaultMessage: 'Premium',
                        id: 'pricingModel.advanceSettings.premiumSuffixLabel'
                      })})`
                    : ''
                }`}
                name="campaign-margin-premium"
                numberOfDecimals={2}
                onChange={value => handleMarginChange('margin', value as string, MediaTypeEnum.pmp)}
                shape="squareUI"
                type="number"
                value={margin.pmp}
              />
            </Switch.Case>
            <Switch.Case condition={mediaType !== MediaTypeEnum.hybrid}>
              <Input
                {...COMMON_INPUT_PROPS}
                description={errors?.margin?.[mediaType]?.message}
                id={`campaign-margin-${id}`}
                inputStatus={errors?.margin?.[mediaType] ? 'wrong' : 'default'}
                label={formatMessage({
                  defaultMessage: 'Campaign Margin',
                  id: 'pricingModel.advanceSettings.campaignMarginLabel'
                })}
                name="campaign-margin"
                numberOfDecimals={2}
                onChange={value => handleMarginChange('margin', value as string)}
                shape="squareUI"
                type="number"
                value={margin[mediaType as MediaTypeEnum.openRtb | MediaTypeEnum.pmp]}
              />
            </Switch.Case>
          </Switch>
        )}
        {campaignMarkupPermission && (
          <Input
            {...COMMON_INPUT_PROPS}
            description={errors?.markup?.message}
            id={`agency-fee-${id}`}
            inputStatus={errors?.markup ? 'wrong' : 'default'}
            label={formatMessage({
              defaultMessage: 'Agency Fee',
              id: 'pricingModel.advanceSettings.agencyFeeLabel'
            })}
            name="agency-fee"
            numberOfDecimals={2}
            onChange={val => handleNumberChange('markup', val as string)}
            shape="squareUI"
            type="number"
            value={markup}
          />
        )}
        {agencyFeeModelPermission && (
          <Selector
            description={errors?.agencyFeeModel?.message}
            id={`agency-fee-model-${id}`}
            label={formatMessage({
              defaultMessage: 'Agency Fee Model',
              id: 'pricingModel.advanceSettings.agencyFeeModelLabel'
            })}
            onChange={val => handleModelChange('agencyFeeModel', val as { id: string })}
            options={agencyFeeModelOptions}
            selectorStatus={errors?.agencyFeeModel ? 'wrong' : 'default'}
            type="simple"
            value={agencyFeeModelSelectedOptions}
          />
        )}
        {platformFeePermission && (
          <Input
            {...COMMON_INPUT_PROPS}
            description={errors?.platformFee?.message}
            id={`platform-fee-${id}`}
            inputStatus={errors?.platformFee ? 'wrong' : 'default'}
            label={formatMessage({
              defaultMessage: 'Platform Fee',
              id: 'pricingModel.advanceSettings.platformFeeLabel'
            })}
            name="platform-fee"
            numberOfDecimals={2}
            onChange={val => handleNumberChange('platformFee', val as string)}
            shape="squareUI"
            type="number"
            value={platformFee}
          />
        )}
        {platformFeeModelPermission && (
          <Selector
            description={errors?.platformFeeModel?.message}
            id={`platform-fee-model-${id}`}
            label={formatMessage({
              defaultMessage: 'Platform Fee Model',
              id: 'pricingModel.advanceSettings.platformFeeModelLabel'
            })}
            onChange={val => handleModelChange('platformFeeModel', val as { id: string })}
            options={platformFeeModelOptions}
            selectorStatus={errors?.platformFeeModel ? 'wrong' : 'default'}
            type="simple"
            value={platformFeeModelSelectedOptions}
          />
        )}
        {marginCPMPermission && (
          <Switch>
            <Switch.Case condition={mediaType === MediaTypeEnum.hybrid}>
              <Input
                {...COMMON_INPUT_PROPS}
                description={errors?.marginCPM?.openRtb?.message}
                id={`campaign-enhanced-open-${id}`}
                inputStatus={errors?.marginCPM?.openRtb ? 'wrong' : 'default'}
                label={`${formatMessage({
                  defaultMessage: 'Campaign Enhanced',
                  id: 'pricingModel.advanceSettings.campaignEnhancedLabel'
                })}${
                  mediaType === MediaTypeEnum.hybrid
                    ? ` (${formatMessage({
                        defaultMessage: 'Open',
                        id: 'pricingModel.advanceSettings.openSuffixLabel'
                      })})`
                    : ''
                }`}
                name="campaign-enhanced-open"
                numberOfDecimals={2}
                onChange={value => handleMarginChange('marginCPM', value as string, MediaTypeEnum.openRtb)}
                shape="squareUI"
                type="number"
                value={marginCPM.openRtb}
              />
              <Input
                {...COMMON_INPUT_PROPS}
                description={errors?.marginCPM?.pmp?.message}
                id={`campaign-enhanced-premium-${id}`}
                inputStatus={errors?.marginCPM?.pmp ? 'wrong' : 'default'}
                label={`${formatMessage({
                  defaultMessage: 'Campaign Enhanced',
                  id: 'pricingModel.advanceSettings.campaignEnhancedLabel'
                })}${
                  mediaType === MediaTypeEnum.hybrid
                    ? ` (${formatMessage({
                        defaultMessage: 'Premium',
                        id: 'pricingModel.advanceSettings.premiumSuffixLabel'
                      })})`
                    : ''
                }`}
                name="campaign-enhanced-premium"
                numberOfDecimals={2}
                onChange={value => handleMarginChange('marginCPM', value as string, MediaTypeEnum.pmp)}
                shape="squareUI"
                type="number"
                value={marginCPM.pmp}
              />
            </Switch.Case>
            <Switch.Case condition={mediaType !== MediaTypeEnum.hybrid}>
              <Input
                {...COMMON_INPUT_PROPS}
                description={errors?.marginCPM?.[mediaType]?.message}
                id={`campaign-enhanced-${id}`}
                inputStatus={errors?.marginCPM?.[mediaType] ? 'wrong' : 'default'}
                label={formatMessage({
                  defaultMessage: 'Campaign Enhanced',
                  id: 'pricingModel.advanceSettings.campaignEnhancedLabel'
                })}
                name="campaign-enhanced"
                onChange={value => handleMarginChange('marginCPM', value as string)}
                shape="squareUI"
                type="number"
                value={marginCPM[mediaType as MediaTypeEnum.openRtb | MediaTypeEnum.pmp]}
              />
            </Switch.Case>
          </Switch>
        )}
        {dCPMModelPermission && (
          <Selector
            description={errors?.dCPMModel?.message}
            id={`platform-fee-model-${id}`}
            isDisabled={pricingModel !== PricingModelEnum['d-cpm']}
            label={formatMessage({
              defaultMessage: 'D-CPM Model',
              id: 'pricingModel.advanceSettings.dCPMModelLabel'
            })}
            onChange={val => handleModelChange('dCPMModel', val as { id: string })}
            options={dCPMOptions}
            selectorStatus={errors?.dCPMModel ? 'wrong' : 'default'}
            type="simple"
            value={dCPMSelectedOptions}
          />
        )}
      </div>
    </PricingAdvanceSettingsStyled>
  );
}

export default PricingAdvanceSettings;
